/**
 * Walkthrough store (PROD-595).
 *
 * Drives the `<DemoWalkthrough>` overlay. Owns:
 *   - `isOpen`            - whether the coachmark surface is rendered
 *   - `currentStepIndex`  - 0-based index into `WALKTHROUGH_STEPS`
 *   - `totalSteps`        - mirror of `WALKTHROUGH_TOTAL_STEPS`
 *   - `source`            - 'auto' | 'manual'; read by the
 *                           `walkthrough_step_view` telemetry payload
 *   - `dismissed`         - whether the user has skipped or completed in
 *                           this session (or any prior session for the
 *                           same userId + tenantId pair)
 *   - `dismissedAtMs`     - timestamp of the last dismissal, persisted
 *                           per (userId, tenantId)
 *   - `correlationId`     - per-session id grouping all
 *                           walkthrough_step_view + completed +
 *                           dismissed events
 *   - `startedAtMs`       - timestamp of the first step view in the
 *                           current session; used to compute
 *                           completion_time_ms
 *   - `persistError`      - transient banner state when localStorage
 *                           setItem throws (mirrors PROD-571 + PROD-568
 *                           revert pattern)
 *
 * Persistence:
 *   - Per ADR-PROTO-006 the localStorage key scope is
 *     `cps:${userId}:walkthrough:${tenantId}`. The persisted payload
 *     is a JSON object with `dismissed_at_ms` (number | null) so the
 *     auto-show heuristic on first visit reads as "dismissed_at_ms is
 *     not present".
 *   - The store hydrates lazily: on every getter that consults the
 *     dismissed flag the latest persisted value for the active
 *     (userId, tenantId) tuple is read. Tests can drive the store via
 *     `setState` without touching localStorage.
 *
 * Telemetry:
 *   - `next` / `prev` / `openManual` / `openAuto` mutations route
 *     through `recordStepView` which emits `walkthrough_step_view` with
 *     the strict payload (HC9 telemetry-real). The schema enforces the
 *     `source` enum + the correlation_id.
 *   - `complete` emits `walkthrough_completed` with the elapsed
 *     milliseconds from `startedAtMs` to now.
 *   - `dismiss` emits `walkthrough_dismissed` with the step id + index
 *     where the user bailed out.
 *
 * Persist-error pattern (mirrors PROD-571 / PROD-591 fixers):
 *   - `dismiss` and `complete` both attempt a localStorage.setItem with
 *     the new dismissed_at_ms. If setItem throws (quota / private
 *     browsing) the in-memory mutation that closed the walkthrough is
 *     reverted (the overlay re-opens on the same step the user was on)
 *     and `persistError` is populated so the relaunch button can render
 *     a non-blocking warning. Telemetry for the dismissed / completed
 *     event is still emitted because the user's intent was to close the
 *     overlay; only the localStorage seam failed.
 *
 * HC5: zero em / en dashes anywhere in this file or in any string the
 * store sets / reads.
 */
import { create } from 'zustand';
import {
  WALKTHROUGH_STEPS,
  WALKTHROUGH_TOTAL_STEPS,
  type WalkthroughStepId,
} from '../data/walkthrough-script';
import { emitTelemetry, newCorrelationId } from '../services/telemetry';
import { useUserStore } from './user.store';
import { useTenantStore } from './tenant.store';

export type WalkthroughSource = 'auto' | 'manual';

const PERSIST_ERROR_MESSAGE =
  'Walkthrough preference could not be persisted. The tour will re-open next visit.';

export interface WalkthroughPersistedShape {
  /** Epoch ms of the most recent dismissal. null = never dismissed. */
  dismissed_at_ms: number | null;
}

export interface WalkthroughStoreState {
  isOpen: boolean;
  currentStepIndex: number;
  totalSteps: number;
  source: WalkthroughSource;
  dismissed: boolean;
  dismissedAtMs: number | null;
  correlationId: string | null;
  startedAtMs: number | null;
  persistError: string | null;

  /** Open the walkthrough at step 0; emit `walkthrough_step_view`. */
  openAuto: () => void;
  /** Manual relaunch (TopBar button); always restarts at step 0. */
  openManual: () => void;
  /** Advance one step. Emits `walkthrough_step_view` for the new index. */
  next: () => void;
  /** Step back one. Emits `walkthrough_step_view` for the new index. */
  prev: () => void;
  /**
   * Skip / dismiss. Closes the overlay, persists `dismissed_at_ms`,
   * emits `walkthrough_dismissed`. Reverts on persist failure.
   */
  dismiss: () => void;
  /**
   * Walkthrough finished (Next on the last step). Closes the overlay,
   * persists `dismissed_at_ms`, emits `walkthrough_completed`.
   */
  complete: () => void;
  /** Clear the transient persist-error banner. */
  clearPersistError: () => void;
  /**
   * Hydrate `dismissed` + `dismissedAtMs` for the active (userId,
   * tenantId) pair. Called on app boot from `main.tsx` (or via the
   * overlay's `useEffect` on first mount).
   */
  hydrate: () => void;
  /** Reset to the pristine state. Used by tests + Reset Demo (Phase 3). */
  reset: () => void;
}

/** localStorage key per ADR-PROTO-006 userId-keying convention. */
export function walkthroughStorageKey(userId: string, tenantId: string): string {
  return `cps:${userId}:walkthrough:${tenantId}`;
}

/**
 * Read the persisted walkthrough payload for the (userId, tenantId).
 * Returns null on any failure path (no value, malformed JSON, no
 * localStorage). Caller treats null as "never dismissed".
 */
export function readPersistedWalkthrough(
  userId: string,
  tenantId: string,
): WalkthroughPersistedShape | null {
  if (typeof localStorage === 'undefined') return null;
  try {
    const raw = localStorage.getItem(walkthroughStorageKey(userId, tenantId));
    if (!raw) return null;
    const parsed = JSON.parse(raw) as WalkthroughPersistedShape;
    if (typeof parsed !== 'object' || parsed === null) return null;
    if (
      parsed.dismissed_at_ms !== null &&
      typeof parsed.dismissed_at_ms !== 'number'
    ) {
      return null;
    }
    return parsed;
  } catch (err) {
    console.warn('[walkthrough.store] localStorage read failed', err);
    return null;
  }
}

/**
 * Persist the dismissed_at_ms for the (userId, tenantId). Throws on
 * underlying setItem failure so the calling action can roll back the
 * in-memory state (HC6 multi-tenant isolation + ADR-PROTO-006 keying).
 */
function persistWalkthrough(
  userId: string,
  tenantId: string,
  payload: WalkthroughPersistedShape,
): void {
  if (typeof localStorage === 'undefined') return;
  localStorage.setItem(
    walkthroughStorageKey(userId, tenantId),
    JSON.stringify(payload),
  );
}

/**
 * Read the current step id for the supplied index. Defensive helper -
 * returns the first step id on out-of-range input so the dismissed
 * telemetry payload always carries a concrete id.
 */
function stepIdAt(index: number): WalkthroughStepId {
  const step = WALKTHROUGH_STEPS[index] ?? WALKTHROUGH_STEPS[0];
  return step.id;
}

export const useWalkthroughStore = create<WalkthroughStoreState>((set, get) => {
  /** Internal: emit walkthrough_step_view with the validated payload. */
  function recordStepView(
    stepIndex: number,
    source: WalkthroughSource,
    correlationId: string,
  ): void {
    const step = WALKTHROUGH_STEPS[stepIndex];
    if (!step) return;
    emitTelemetry('walkthrough_step_view', {
      step_id: step.id,
      step_index: stepIndex,
      total_steps: WALKTHROUGH_TOTAL_STEPS,
      source,
      correlation_id: correlationId,
    });
  }

  return {
    isOpen: false,
    currentStepIndex: 0,
    totalSteps: WALKTHROUGH_TOTAL_STEPS,
    source: 'auto',
    dismissed: false,
    dismissedAtMs: null,
    correlationId: null,
    startedAtMs: null,
    persistError: null,

    openAuto: () => {
      const correlationId = newCorrelationId();
      const now =
        typeof performance !== 'undefined' ? performance.now() : Date.now();
      set({
        isOpen: true,
        currentStepIndex: 0,
        source: 'auto',
        correlationId,
        startedAtMs: now,
        persistError: null,
      });
      recordStepView(0, 'auto', correlationId);
    },

    openManual: () => {
      const correlationId = newCorrelationId();
      const now =
        typeof performance !== 'undefined' ? performance.now() : Date.now();
      set({
        isOpen: true,
        currentStepIndex: 0,
        source: 'manual',
        correlationId,
        startedAtMs: now,
        persistError: null,
        // Manual relaunch resets the dismissed flag for the current
        // session so the overlay does not auto-close itself.
        dismissed: false,
      });
      recordStepView(0, 'manual', correlationId);
    },

    next: () => {
      const { currentStepIndex, source, correlationId, totalSteps } = get();
      const nextIndex = currentStepIndex + 1;
      if (nextIndex >= totalSteps) {
        get().complete();
        return;
      }
      const corr = correlationId ?? newCorrelationId();
      set({ currentStepIndex: nextIndex, correlationId: corr });
      recordStepView(nextIndex, source, corr);
    },

    prev: () => {
      const { currentStepIndex, source, correlationId } = get();
      const prevIndex = currentStepIndex - 1;
      if (prevIndex < 0) return;
      const corr = correlationId ?? newCorrelationId();
      set({ currentStepIndex: prevIndex, correlationId: corr });
      recordStepView(prevIndex, source, corr);
    },

    dismiss: () => {
      const { currentStepIndex, correlationId } = get();
      const corr = correlationId ?? newCorrelationId();
      const now = Date.now();
      const stepId = stepIdAt(currentStepIndex);

      // Snapshot the open state so we can revert on persist failure.
      const previousIsOpen = get().isOpen;
      const previousDismissed = get().dismissed;
      const previousDismissedAt = get().dismissedAtMs;

      set({
        isOpen: false,
        dismissed: true,
        dismissedAtMs: now,
        persistError: null,
      });

      try {
        const userId = useUserStore.getState().userId;
        const tenantId = useTenantStore.getState().tenantId;
        persistWalkthrough(userId, tenantId, { dismissed_at_ms: now });
      } catch (err) {
        console.warn(
          '[walkthrough.store] dismiss persist failed; reverting close',
          err,
        );
        set({
          isOpen: previousIsOpen,
          dismissed: previousDismissed,
          dismissedAtMs: previousDismissedAt,
          persistError: PERSIST_ERROR_MESSAGE,
        });
        // Telemetry still fires below: the user's intent was to dismiss;
        // only the persistence seam failed. The overlay re-opens so the
        // user can retry or skip again.
      }

      emitTelemetry('walkthrough_dismissed', {
        step_id: stepId,
        step_index: currentStepIndex,
        correlation_id: corr,
      });
    },

    complete: () => {
      const { correlationId, startedAtMs, totalSteps } = get();
      const corr = correlationId ?? newCorrelationId();
      const now =
        typeof performance !== 'undefined' ? performance.now() : Date.now();
      const elapsed = startedAtMs !== null ? Math.max(0, now - startedAtMs) : 0;
      const dismissedAt = Date.now();

      const previousIsOpen = get().isOpen;
      const previousDismissed = get().dismissed;
      const previousDismissedAt = get().dismissedAtMs;

      set({
        isOpen: false,
        dismissed: true,
        dismissedAtMs: dismissedAt,
        persistError: null,
      });

      try {
        const userId = useUserStore.getState().userId;
        const tenantId = useTenantStore.getState().tenantId;
        persistWalkthrough(userId, tenantId, {
          dismissed_at_ms: dismissedAt,
        });
      } catch (err) {
        console.warn(
          '[walkthrough.store] complete persist failed; reverting close',
          err,
        );
        set({
          isOpen: previousIsOpen,
          dismissed: previousDismissed,
          dismissedAtMs: previousDismissedAt,
          persistError: PERSIST_ERROR_MESSAGE,
        });
      }

      emitTelemetry('walkthrough_completed', {
        total_steps: totalSteps,
        completion_time_ms: Math.round(elapsed),
        correlation_id: corr,
      });
    },

    clearPersistError: () => {
      if (get().persistError !== null) {
        set({ persistError: null });
      }
    },

    hydrate: () => {
      const userId = useUserStore.getState().userId;
      const tenantId = useTenantStore.getState().tenantId;
      const persisted = readPersistedWalkthrough(userId, tenantId);
      if (persisted && persisted.dismissed_at_ms !== null) {
        set({ dismissed: true, dismissedAtMs: persisted.dismissed_at_ms });
      } else {
        set({ dismissed: false, dismissedAtMs: null });
      }
    },

    reset: () => {
      set({
        isOpen: false,
        currentStepIndex: 0,
        source: 'auto',
        dismissed: false,
        dismissedAtMs: null,
        correlationId: null,
        startedAtMs: null,
        persistError: null,
      });
    },
  };
});

export { PERSIST_ERROR_MESSAGE as WALKTHROUGH_PERSIST_ERROR_MESSAGE };
